/**
 * Seeds Action Handlers Hook
 *
 * Implements business logic for bulk seed operations:
 * - Adding comments to seeds
 * - Status changes (approve, reject, activate, freeze, delete)
 * - Security scans (web crawl, web scan, network scan, port scan, comprehensive, affiliation)
 * - Sharing seed URLs
 * - Exporting seeds (opens modal)
 *
 * Pattern: Follows Technology section's three-layer composition pattern
 * Risk Level: Medium
 * - Async operations with error handling
 * - Multiple external dependencies
 * - Uses Promise.allSettled for graceful failure handling
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useUpdatePreseed } from '@/hooks/usePreseed';
import {
  useSeedCommentHandlers,
  useSeedScanHandlers,
  useSeedShareHandlers,
  useSeedStatusHandlers,
} from '@/sections/seeds/hooks/actions';
import type { Preseed, Seed } from '@/types';
import { SeedStatus } from '@/types';
import { pluralize } from '@/utils/pluralize.util';

/**
 * Configuration for action handlers
 */
export interface SeedsActionHandlerConfig {
  /** Currently selected seeds */
  selectedSeeds: Seed[];
  /** Function to invalidate/refetch seed data */
  invalidateSeeds: () => void;
  /** Function to set confirm status (for success/error feedback) */
  setConfirmStatus: (status: unknown) => void;
  /** Current bulk selection state — forwarded to useSeedStatusHandlers for bulk path detection */
  selectionState?: BulkSelectionState;
  /** Called when bulk path is triggered; caller opens BulkConfirmationDialog */
  onBulkRequested?: (status: SeedStatus) => void;
}

/**
 * Unified interface for all seed action handlers
 */
export interface SeedsActionHandlers {
  /** Add comment to selected seeds */
  handleComment: (comment: string) => Promise<void>;
  /** Approve selected seeds (Accept status) */
  handleApprove: () => Promise<void>;
  /** Reject selected seeds */
  handleReject: () => Promise<void>;
  /** Activate selected seeds (Accept status - same as approve) */
  handleActivate: () => Promise<void>;
  /** Freeze selected seeds */
  handleFreeze: () => Promise<void>;
  /** Delete selected seeds */
  handleDelete: () => Promise<void>;
  /** Run web crawl on web application seeds */
  handleWebCrawl: () => void;
  /** Run web scan (DAST) on web application seeds */
  handleWebScan: () => Promise<void>;
  /** Run comprehensive scan on seeds */
  handleComprehensiveScan: () => Promise<void>;
  /** Run affiliation scan on seeds */
  handleAffiliationScan: () => Promise<void>;
  /** Run network vulnerability scan on domain/IP seeds */
  handleNetworkScan: () => Promise<void>;
  /** Run port scan on domain/IP seeds */
  handlePortScan: () => Promise<void>;
  /** Share seed URLs by copying to clipboard */
  handleShare: () => void;
  /** Open export modal (modal state managed by coordinator) */
  handleExport: () => void;
}

/**
 * Custom hook for seeds action business logic
 *
 * Composes existing action handlers into a unified interface.
 * Each handler is responsible for its own error handling and toast notifications.
 *
 * Example usage:
 * ```tsx
 * const handlers = useSeedsActionHandlers({
 *   selectedSeeds,
 *   invalidateSeeds,
 *   setConfirmStatus,
 * });
 *
 * await handlers.handleComment('Important note');
 * await handlers.handleApprove();
 * await handlers.handleWebScan();
 * ```
 *
 * @param config - Configuration with selected seeds and callbacks
 * @returns Object containing all action handlers
 */
/**
 * Type guard to check if a seed is actually a preseed
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPreseed = (seed: any): seed is Preseed => {
  return seed && 'type' in seed && !('class' in seed);
};

export const useSeedsActionHandlers = ({
  selectedSeeds,
  invalidateSeeds,
  setConfirmStatus,
  selectionState,
  onBulkRequested,
}: SeedsActionHandlerConfig): SeedsActionHandlers => {
  // Get comment handlers
  const { handleAddComment } = useSeedCommentHandlers();

  // Get status handlers for regular seeds — pass bulk context through
  const {
    handleApprove: handleApproveStatus,
    handleReject: handleRejectStatus,
    handleFreeze: handleFreezeStatus,
    handleDelete: handleDeleteStatus,
  } = useSeedStatusHandlers({ selectionState, onBulkRequested });

  // Get preseed update handler
  const { mutateAsync: updatePreseed } = useUpdatePreseed();

  // Get scan handlers
  const {
    handleWebCrawl: handleWebCrawlScan,
    handleWebScan: handleWebScanScan,
    handleComprehensiveScan: handleComprehensiveScanScan,
    handleAffiliationScan: handleAffiliationScanScan,
    handleNetworkScan: handleNetworkScanScan,
    handlePortScan: handlePortScanScan,
  } = useSeedScanHandlers();

  // Get share handler
  const { handleShare: handleShareUrls } = useSeedShareHandlers(selectedSeeds);

  /**
   * Add comment to selected seeds
   * Determines seed type from first selected seed's class
   */
  const handleComment = useCallback(
    async (comment: string) => {
      if (selectedSeeds.length === 0) return;

      // Determine seed type from first seed
      const seedType =
        selectedSeeds[0]?.class === 'webapplication'
          ? 'webapplication'
          : selectedSeeds[0]?.class === 'ipv4' ||
              selectedSeeds[0]?.class === 'ipv6' ||
              selectedSeeds[0]?.class === 'cidr'
            ? 'ip'
            : 'domain';

      // Type assertion needed due to Seed type differences between imports
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await handleAddComment(selectedSeeds as any, comment, seedType);
      invalidateSeeds();
    },
    [selectedSeeds, handleAddComment, invalidateSeeds]
  );

  /**
   * Approve selected seeds (handles both regular seeds and preseeds)
   */
  const handleApprove = useCallback(async () => {
    if (selectedSeeds.length === 0) return;

    // Check if we're dealing with preseeds
    if (isPreseed(selectedSeeds[0])) {
      // Use preseed-specific update logic
      const promises = (selectedSeeds as unknown as Preseed[]).map(({ key }) =>
        updatePreseed({ key, status: SeedStatus.Accept })
      );

      const results = await Promise.allSettled(promises);
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (succeeded > 0) {
        toast.success(
          `Approved ${succeeded} ${pluralize(succeeded, 'attribute')}`
        );
      }
      if (failed > 0) {
        toast.error(
          `Failed to approve ${failed} ${pluralize(failed, 'attribute')}`
        );
      }

      invalidateSeeds();
      setConfirmStatus({ type: 'success', count: succeeded });
      return;
    }

    // Regular seed logic
    const seedType =
      selectedSeeds[0]?.class === 'webapplication'
        ? 'webapplication'
        : selectedSeeds[0]?.class === 'ipv4' ||
            selectedSeeds[0]?.class === 'ipv6' ||
            selectedSeeds[0]?.class === 'cidr'
          ? 'ip'
          : 'domain';

    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleApproveStatus(selectedSeeds as any, seedType);
    invalidateSeeds();
    setConfirmStatus({ type: 'success', count: selectedSeeds.length });
  }, [
    selectedSeeds,
    handleApproveStatus,
    updatePreseed,
    invalidateSeeds,
    setConfirmStatus,
  ]);

  /**
   * Reject selected seeds (handles both regular seeds and preseeds)
   */
  const handleReject = useCallback(async () => {
    if (selectedSeeds.length === 0) return;

    // Check if we're dealing with preseeds
    if (isPreseed(selectedSeeds[0])) {
      // Use preseed-specific update logic
      const promises = (selectedSeeds as unknown as Preseed[]).map(({ key }) =>
        updatePreseed({ key, status: SeedStatus.Reject })
      );

      const results = await Promise.allSettled(promises);
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (succeeded > 0) {
        toast.success(
          `Rejected ${succeeded} ${pluralize(succeeded, 'attribute')}`
        );
      }
      if (failed > 0) {
        toast.error(
          `Failed to reject ${failed} ${pluralize(failed, 'attribute')}`
        );
      }

      invalidateSeeds();
      setConfirmStatus({ type: 'success', count: succeeded });
      return;
    }

    // Regular seed logic
    const seedType =
      selectedSeeds[0]?.class === 'webapplication'
        ? 'webapplication'
        : selectedSeeds[0]?.class === 'ipv4' ||
            selectedSeeds[0]?.class === 'ipv6' ||
            selectedSeeds[0]?.class === 'cidr'
          ? 'ip'
          : 'domain';

    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleRejectStatus(selectedSeeds as any, seedType);
    invalidateSeeds();
    setConfirmStatus({ type: 'success', count: selectedSeeds.length });
  }, [
    selectedSeeds,
    handleRejectStatus,
    updatePreseed,
    invalidateSeeds,
    setConfirmStatus,
  ]);

  /**
   * Activate selected seeds (same as approve)
   */
  const handleActivate = useCallback(async () => {
    await handleApprove();
  }, [handleApprove]);

  /**
   * Freeze selected seeds
   */
  const handleFreeze = useCallback(async () => {
    if (selectedSeeds.length === 0) return;

    const seedType =
      selectedSeeds[0]?.class === 'webapplication'
        ? 'webapplication'
        : selectedSeeds[0]?.class === 'ipv4' ||
            selectedSeeds[0]?.class === 'ipv6' ||
            selectedSeeds[0]?.class === 'cidr'
          ? 'ip'
          : 'domain';

    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleFreezeStatus(selectedSeeds as any, seedType);
    invalidateSeeds();
    setConfirmStatus({ type: 'success', count: selectedSeeds.length });
  }, [selectedSeeds, handleFreezeStatus, invalidateSeeds, setConfirmStatus]);

  /**
   * Delete selected seeds
   */
  const handleDelete = useCallback(async () => {
    if (selectedSeeds.length === 0) return;

    const seedType =
      selectedSeeds[0]?.class === 'webapplication'
        ? 'webapplication'
        : selectedSeeds[0]?.class === 'ipv4' ||
            selectedSeeds[0]?.class === 'ipv6' ||
            selectedSeeds[0]?.class === 'cidr'
          ? 'ip'
          : 'domain';

    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleDeleteStatus(selectedSeeds as any, seedType);
    invalidateSeeds();
    setConfirmStatus({ type: 'success', count: selectedSeeds.length });
  }, [selectedSeeds, handleDeleteStatus, invalidateSeeds, setConfirmStatus]);

  /**
   * Scan actions - pass through to scan handlers
   */
  const handleWebCrawl = useCallback(() => {
    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleWebCrawlScan(selectedSeeds as any);
  }, [selectedSeeds, handleWebCrawlScan]);

  const handleWebScan = useCallback(async () => {
    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleWebScanScan(selectedSeeds as any);
  }, [selectedSeeds, handleWebScanScan]);

  const handleComprehensiveScan = useCallback(async () => {
    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleComprehensiveScanScan(selectedSeeds as any);
  }, [selectedSeeds, handleComprehensiveScanScan]);

  const handleAffiliationScan = useCallback(async () => {
    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleAffiliationScanScan(selectedSeeds as any);
  }, [selectedSeeds, handleAffiliationScanScan]);

  const handleNetworkScan = useCallback(async () => {
    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleNetworkScanScan(selectedSeeds as any);
  }, [selectedSeeds, handleNetworkScanScan]);

  const handlePortScan = useCallback(async () => {
    // Type assertion needed due to Seed type differences between imports
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handlePortScanScan(selectedSeeds as any);
  }, [selectedSeeds, handlePortScanScan]);

  /**
   * Share seed URLs
   */
  const handleShare = useCallback(() => {
    handleShareUrls();
  }, [handleShareUrls]);

  /**
   * Export seeds (opens modal)
   * Modal state managed by coordinator hook
   */
  const handleExport = useCallback(() => {
    // Export modal will be opened by coordinator
  }, []);

  return {
    handleComment,
    handleApprove,
    handleReject,
    handleActivate,
    handleFreeze,
    handleDelete,
    handleWebCrawl,
    handleWebScan,
    handleComprehensiveScan,
    handleAffiliationScan,
    handleNetworkScan,
    handlePortScan,
    handleShare,
    handleExport,
  };
};
